import React, { useEffect, useMemo, useState } from 'react';
import { Check, Lightbulb, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { useQuickReplies } from '../../hooks/useQuickReplies';
import { fetchStageNameForStatus } from '../../services/leadsService';
import { suggestionsForStage } from '../../lib/messageSuggestions';
import {
    DEFAULT_QUICK_REPLIES, QUICK_REPLY_VARIABLES, QuickReply,
    applyQuickReplyVariables, filterQuickReplies,
} from '../../lib/quickReplies';
import { confirmAction } from '../../lib/dialogs';

interface Props {
    companyId: number | null | undefined;
    companyName?: string | null;
    leadName?: string | null;
    /** Etapa do lead (status) e nicho: definem quais mensagens são indicadas. */
    leadStatus?: string | null;
    niche?: string | null;
    onPick: (text: string) => void;
}

type Draft = { id: number | null; title: string; body: string; shared: boolean };
const EMPTY: Draft = { id: null, title: '', body: '', shared: false };

/** Lista de mensagens rápidas do chat: clicar insere no campo; dá para criar, editar e apagar. */
export const QuickRepliesPicker: React.FC<Props> = ({ companyId, companyName, leadName, leadStatus, niche, onPick }) => {
    const { user, isSuperAdmin } = useAuth();
    const { replies, loading, create, update, remove } = useQuickReplies(companyId, user?.id);
    const [query, setQuery] = useState('');
    const [draft, setDraft] = useState<Draft | null>(null);
    const [saving, setSaving] = useState(false);
    const [stageName, setStageName] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setStageName(null);
        if (leadStatus && leadStatus !== 'new') {
            fetchStageNameForStatus(leadStatus).then(n => { if (!cancelled) setStageName(n); }).catch(() => undefined);
        } else if (leadStatus === 'new') {
            setStageName('novo');
        }
        return () => { cancelled = true; };
    }, [leadStatus]);

    // Mensagens indicadas para o momento do lead; as que a pessoa já salvou com o mesmo texto não repetem
    const suggestions = useMemo(
        () => suggestionsForStage(stageName, niche, { limit: 3 }),
        [stageName, niche],
    );

    const canManageShared = isSuperAdmin || ['gestor', 'ceo', 'admin'].includes(user?.role || '');
    const canManage = (r: QuickReply) => r.owner_id === user?.id || (r.owner_id === null && canManageShared);
    const visible = useMemo(() => filterQuickReplies(replies, query), [replies, query]);
    const ctx = { leadName, userName: user?.name, companyName };

    const save = async () => {
        if (!draft) return;
        if (!draft.title.trim() || !draft.body.trim()) {
            toast.error('Preencha o título e o texto da mensagem.');
            return;
        }
        setSaving(true);
        try {
            if (draft.id === null) await create({ title: draft.title, body: draft.body, shared: draft.shared });
            else await update(draft.id, { title: draft.title, body: draft.body });
            toast.success(draft.id === null ? 'Mensagem criada.' : 'Mensagem atualizada.');
            setDraft(null);
        } catch (err) {
            console.error('Erro ao salvar mensagem rápida:', err);
            toast.error('Não foi possível salvar a mensagem.');
        } finally {
            setSaving(false);
        }
    };

    const del = async (r: QuickReply) => {
        if (!await confirmAction(`Apagar a mensagem "${r.title}"?${r.owner_id === null ? '\nEla some para toda a equipe.' : ''}`)) return;
        try {
            await remove(r.id);
            toast.success('Mensagem apagada.');
        } catch (err) {
            console.error('Erro ao apagar mensagem rápida:', err);
            toast.error('Não foi possível apagar a mensagem.');
        }
    };

    const saveSuggestion = async (title: string, body: string) => {
        try {
            await create({ title, body, shared: false });
            toast.success('Salva nas suas mensagens rápidas.');
        } catch (err) {
            console.error('Erro ao salvar sugestão:', err);
            toast.error('Não foi possível salvar a mensagem.');
        }
    };

    const addDefaults = async () => {
        setSaving(true);
        try {
            for (const d of DEFAULT_QUICK_REPLIES) await create({ ...d, shared: false });
            toast.success('Sugestões adicionadas às suas mensagens.');
        } catch (err) {
            console.error('Erro ao adicionar sugestões:', err);
            toast.error('Não foi possível adicionar as sugestões.');
        } finally {
            setSaving(false);
        }
    };

    const input = 'w-full px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500';

    return (
        <div className="absolute bottom-full left-0 mb-2 w-80 max-w-[90vw] bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-2 z-50">
            {draft ? (
                <div className="space-y-2 p-1">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-500">{draft.id === null ? 'Nova mensagem rápida' : 'Editar mensagem rápida'}</span>
                        <button type="button" aria-label="Cancelar" onClick={() => setDraft(null)} className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"><X size={14} /></button>
                    </div>
                    <input autoFocus maxLength={60} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="Título (ex.: Orçamento enviado)" className={input} />
                    <textarea rows={4} maxLength={4000} value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} placeholder="Texto da mensagem" className={`${input} resize-none`} />
                    <div className="flex flex-wrap gap-1">
                        {QUICK_REPLY_VARIABLES.map(v => (
                            <button key={v.token} type="button" title={v.label} onClick={() => setDraft({ ...draft, body: `${draft.body}${draft.body && !draft.body.endsWith(' ') ? ' ' : ''}${v.token}` })}
                                className="px-2 py-0.5 text-[11px] rounded-full bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800/50 hover:bg-yellow-100">
                                {v.token}
                            </button>
                        ))}
                    </div>
                    {draft.id === null && canManageShared && (
                        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                            <input type="checkbox" checked={draft.shared} onChange={e => setDraft({ ...draft, shared: e.target.checked })} className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" />
                            Compartilhar com toda a equipe
                        </label>
                    )}
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={() => setDraft(null)} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg">Cancelar</button>
                        <button type="button" onClick={save} disabled={saving} className="px-3 py-1.5 text-xs font-semibold bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1">
                            <Check size={13} /> Salvar
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <div className="relative flex-1">
                            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar mensagem..." className={`${input} pl-7 py-1`} />
                        </div>
                        <button type="button" onClick={() => setDraft(EMPTY)} className="px-2 py-1 text-xs font-semibold text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg inline-flex items-center gap-1 whitespace-nowrap">
                            <Plus size={13} /> Nova
                        </button>
                    </div>

                    {!query && suggestions.length > 0 && (
                        <div className="mb-2 rounded-lg bg-yellow-50/70 dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-900/30 p-1.5">
                            <p className="flex items-center gap-1 px-1.5 pb-1 text-[11px] font-semibold text-yellow-800 dark:text-yellow-300">
                                <Lightbulb size={12} /> Indicadas{stageName ? <> para esta etapa</> : null}
                            </p>
                            {suggestions.map(sg => {
                                const alreadySaved = replies.some(r => r.body.trim() === sg.body.trim());
                                return (
                                    <div key={sg.id} className="group flex items-start gap-1 rounded-md hover:bg-yellow-100/60 dark:hover:bg-yellow-900/20">
                                        <button type="button" onClick={() => onPick(applyQuickReplyVariables(sg.body, ctx))} className="flex-1 min-w-0 text-left px-2 py-1.5">
                                            <span className="block text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{sg.title}</span>
                                            <span className="block text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2">{applyQuickReplyVariables(sg.body, ctx)}</span>
                                        </button>
                                        {!alreadySaved && (
                                            <button type="button" aria-label={`Salvar "${sg.title}" nas minhas mensagens`} title="Salvar nas minhas mensagens" onClick={() => saveSuggestion(sg.title, sg.body)} className="p-1 mt-1.5 mr-1 text-gray-500 hover:text-yellow-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                                                <Plus size={13} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="max-h-56 overflow-y-auto space-y-0.5">
                        {replies.length === 0 && !loading ? (
                            <div className="px-3 py-4 text-center space-y-2">
                                <p className="text-xs text-gray-500">Você ainda não tem mensagens rápidas.</p>
                                <button type="button" onClick={addDefaults} disabled={saving} className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 hover:underline disabled:opacity-50">
                                    Adicionar {DEFAULT_QUICK_REPLIES.length} sugestões
                                </button>
                            </div>
                        ) : visible.length === 0 ? (
                            <p className="px-3 py-4 text-center text-xs text-gray-500">{loading ? 'Carregando…' : 'Nenhuma mensagem encontrada.'}</p>
                        ) : visible.map(r => (
                            <div key={r.id} className="group flex items-start gap-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700">
                                <button type="button" onClick={() => onPick(applyQuickReplyVariables(r.body, ctx))} className="flex-1 min-w-0 text-left px-3 py-2">
                                    <span className="flex items-center gap-1.5">
                                        <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{r.title}</span>
                                        {r.owner_id === null && <span className="shrink-0 text-[11px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">Equipe</span>}
                                    </span>
                                    <span className="block text-xs text-gray-500 dark:text-gray-400 line-clamp-2 whitespace-pre-line">{r.body}</span>
                                </button>
                                {canManage(r) && (
                                    <span className="flex shrink-0 pt-2 pr-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                        <button type="button" aria-label={`Editar ${r.title}`} title="Editar" onClick={() => setDraft({ id: r.id, title: r.title, body: r.body, shared: r.owner_id === null })} className="p-1 text-gray-500 hover:text-blue-600"><Pencil size={13} /></button>
                                        <button type="button" aria-label={`Apagar ${r.title}`} title="Apagar" onClick={() => del(r)} className="p-1 text-gray-500 hover:text-red-600"><Trash2 size={13} /></button>
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
